import get from 'lodash/get';

import type { EbmResourceConfigurationRecord } from '../../../capabilities/ebm-nodes/types';
import { SYSTEM_DISK_NAME } from '../../../capabilities/image-vm/constants';
import type { SystemDiskRecord } from '../../../capabilities/image-vm/types';
import { normalizeSystemDiskRecord } from '../../../capabilities/image-vm/utils';
import { deserializeServiceListFromApi } from '../../../capabilities/service/serviceApiField';
import { resolveStorageClassSubmitValue } from '../../../components/storageClassField';
import type { PipelineContext } from '../../../core/types';
import { buildDefaultDirectResourceModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import { hasMeaningfulValue } from '../../../utils';
import { resolveEnabledModulesForSpecFilter } from '../../_shared/common/sku/resolveEnabledModulesForSpecFilter';
import { filterEbmSpecByEnabledModules } from './enabledModules';
import type { EbmSpec } from './schema';

/** 将 API/JSON 中的 null / 非数组字段规范为可安全写入 schema 的值 */
function normalizeResourceBundle(parsed: Record<string, unknown>): EbmResourceConfigurationRecord {
  const asRecord = (value: unknown): Record<string, unknown> | undefined =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;

  return {
    key: typeof parsed.key === 'string' ? parsed.key : '',
    nodes: Array.isArray(parsed.nodes) ? (parsed.nodes as string[]) : [],
    cpu: asRecord(parsed.cpu) ?? ({} as Record<string, unknown>),
    memory: asRecord(parsed.memory) ?? ({} as Record<string, unknown>),
    gpu: asRecord(parsed.gpu) ?? ({} as Record<string, unknown>),
    // 接口可能返回 null，统一转空数组，避免 Zod optional 因 null 校验失败
    storage: Array.isArray(parsed.storage) ? (parsed.storage as Record<string, unknown>[]) : [],
    nic: Array.isArray(parsed.nic) ? (parsed.nic as Record<string, unknown>[]) : [],
  };
}

export function parseEbmResourceConfiguration(raw: unknown): EbmResourceConfigurationRecord | undefined {
  if (!hasMeaningfulValue(raw) || typeof raw !== 'string') return undefined;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
    return normalizeResourceBundle(parsed as Record<string, unknown>);
  } catch {
    return undefined;
  }
}

export function flattenDnsNameservers(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'server' in item) {
        return String((item as { server?: string }).server || '');
      }
      return '';
    })
    .filter(Boolean);
}

export function flattenDnsSearches(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'search' in item) {
        return String((item as { search?: string }).search || '');
      }
      return '';
    })
    .filter(Boolean);
}

export function toFormDnsNameservers(raw: unknown): { server: string }[] {
  return flattenDnsNameservers(raw).map((server) => ({ server }));
}

export function toFormDnsSearches(raw: unknown): { search: string }[] {
  return flattenDnsSearches(raw).map((search) => ({ search }));
}

function formatGi(size: number | string | undefined): string | undefined {
  if (size === undefined || size === null || size === '') return undefined;
  const text = String(size);
  return text.endsWith('Gi') ? text : `${text}Gi`;
}

export function serializeEbmSystemDiskVolume(disk: SystemDiskRecord | undefined, creator: string) {
  if (!disk?.diskSize) {
    return { volumes: [] as Record<string, unknown>[], system: undefined, image: undefined, source: undefined };
  }

  const diskSize = formatGi(disk.diskSize);
  const image = disk.image;
  const source = image?.source;
  const storageClassName = resolveStorageClassSubmitValue(disk.storageClass, disk.pricing);

  if (disk.sourceKind === 'existing') {
    return {
      volumes: [
        {
          name: SYSTEM_DISK_NAME,
          type: 'System',
          size: diskSize,
          storageClassName,
          reclaimPolicy: disk.reclaimPolicy,
          source: {
            type: 'Volume',
            externalDisk: {
              name: disk.diskName,
              nickname: disk.diskNickname || disk.diskName,
            },
          },
        },
      ],
      system: image
        ? {
            architecture: image.architecture,
            os: image.os,
            version: image.version,
            bootloader: image.bootloader,
          }
        : undefined,
      image: image?.url,
      source,
    };
  }

  return {
    volumes: [
      {
        name: SYSTEM_DISK_NAME,
        type: 'System',
        size: diskSize,
        storageClassName,
        reclaimPolicy: disk.reclaimPolicy,
        volumeTemplate: {
          spec: {
            creator,
            name: disk.diskName || SYSTEM_DISK_NAME,
            pvc: {
              resources: {
                requests: {
                  storage: diskSize,
                },
              },
              storageClassName,
            },
            source: {
              http: {
                url: image?.url,
              },
              type: 'HTTP',
            },
          },
        },
      },
    ],
    system: image
      ? {
          architecture: image.architecture,
          os: image.os,
          version: image.version,
          bootloader: image.bootloader,
        }
      : undefined,
    image: image?.url,
    source,
  };
}

export function ebmFromForm(values: Record<string, unknown>, ctx: PipelineContext): EbmSpec {
  const bundle = parseEbmResourceConfiguration(values.resourceConfiguration);
  const networkRaw = get(values, 'resource.network', {}) as Record<string, unknown>;
  const dnsConfig = get(networkRaw, 'dnsConfig', {}) as Record<string, unknown>;
  const optionMeta = get(values, 'optionMeta');

  const spec: EbmSpec = {
    alias: String(values.name ?? values.alias ?? ''),
    description: values.description ? String(values.description) : undefined,
    runStrategy: String(values.runStrategy ?? 'Always'),
    workspace: values.workspace ? String(values.workspace) : undefined,
    datacenter: values.datacenter ? String(values.datacenter) : undefined,
    ...(optionMeta && typeof optionMeta === 'object' && !Array.isArray(optionMeta)
      ? { optionMeta: optionMeta as Record<string, unknown> }
      : {}),
    systemDisk: normalizeSystemDiskRecord(values.systemDisk),
    resourceConfigurationRaw: typeof values.resourceConfiguration === 'string' ? values.resourceConfiguration : undefined,
    resource: {
      // 空对象视为未配置，避免把占位 {} 写入 spec
      cpu: bundle?.cpu && Object.keys(bundle.cpu).length ? bundle.cpu : undefined,
      memory: bundle?.memory && Object.keys(bundle.memory).length ? bundle.memory : undefined,
      gpu: bundle?.gpu && Object.keys(bundle.gpu).length ? bundle.gpu : undefined,
      network: {
        vpc: networkRaw.vpc != null ? String(networkRaw.vpc) : undefined,
        subnet: networkRaw.subnet != null ? String(networkRaw.subnet) : 'default',
        securitygroup: networkRaw.securitygroup != null ? String(networkRaw.securitygroup) : undefined,
        dnsConfig: {
          nameservers: flattenDnsNameservers(dnsConfig.nameservers),
          searches: flattenDnsSearches(dnsConfig.searches),
          options: Array.isArray(dnsConfig.options) ? (dnsConfig.options as { name: string; value: string }[]) : undefined,
        },
        service: Array.isArray(networkRaw.service) ? (networkRaw.service as EbmSpec['resource']['network']['service']) : undefined,
        nic: bundle?.nic?.length ? bundle.nic : undefined,
      },
      storage: bundle?.storage?.length ? { device: bundle.storage } : undefined,
    },
  };

  const enabledModules = resolveEnabledModulesForSpecFilter(ctx, values);
  return filterEbmSpecByEnabledModules(spec, enabledModules, ctx.sku?.skuType);
}

export function ebmToForm(spec: EbmSpec, _ctx?: PipelineContext): Record<string, unknown> {
  const dns = spec.resource.network.dnsConfig;
  return {
    name: spec.alias,
    alias: spec.alias,
    description: spec.description,
    runStrategy: spec.runStrategy,
    workspace: spec.workspace,
    datacenter: spec.datacenter,
    ...(spec.optionMeta ? { optionMeta: spec.optionMeta } : {}),
    systemDisk: spec.systemDisk,
    resourceConfiguration: spec.resourceConfigurationRaw,
    resource: {
      network: {
        vpc: spec.resource.network.vpc,
        subnet: spec.resource.network.subnet ?? 'default',
        securitygroup: spec.resource.network.securitygroup,
        service: deserializeServiceListFromApi(spec.resource.network.service as unknown[] | undefined),
        dnsConfig: {
          nameservers: toFormDnsNameservers(dns?.nameservers),
          searches: toFormDnsSearches(dns?.searches),
          options: dns?.options ?? [],
        },
      },
    },
  };
}

export function ebmGetInitialFormValues(ctx?: PipelineContext): Record<string, unknown> {
  const datacenter = ctx?.cluster;
  return {
    ...(datacenter ? { datacenter } : {}),
    runStrategy: 'Always',
    skuResourceModules: buildDefaultDirectResourceModules('ElasticBareMetal'),
    resource: {
      network: {
        subnet: 'default',
        dnsConfig: {
          nameservers: [],
          searches: [],
          options: [],
        },
      },
    },
  };
}
